class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (this.classList.contains('active')) return;
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true },
    );

    document.body.classList.add('overflow-hidden');

    // cart-drawer-items is a CartItems subclass that extends createViewEventElement.
    // Its `view-event-trigger="manual"` skips auto-dispatch on connect; we fire
    // it here when the drawer opens, with `context: 'dialog'` from the payload attribute.
    this.querySelector('cart-drawer-items')?.dispatchViewEvent();
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      // section name first, DOM id only as the legacy fallback
      const sectionHTML = parsedState.sections[section.section || section.id];
      if (!sectionHTML) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(sectionHTML, section.selector);
    });
    if (window.ehpCartTimerBoot) window.ehpCartTimerBoot();

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        // ⚠️ `id` is the DOM TARGET, `section` is the SECTION NAME. Dawn treated
        // them as the same string; they are not any more, because the count now
        // lives in #cart-icon-bubble-count so the refresh cannot wipe the
        // merchant's header icon. Both keys are required — dropping `section`
        // requests a section that does not exist and kills add-to-cart.
        id: 'cart-icon-bubble-count',
        section: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

// EcomHub Pro: cart reservation timer.
// This lives here, in a persistent <script src>, NOT inline in the timer snippet.
// The drawer re-renders through the Section Rendering API (innerHTML), which never
// executes injected <script> tags - so an inline timer script only ever ran on the
// first page load, and only when the cart already had items. On the normal
// empty-cart -> add-to-cart flow it never ran, leaving the clock frozen at 00:00 on
// the live storefront (the editor hid the bug by re-executing section scripts on
// shopify:section:load). Defining the boot here means it is always available and is
// re-run after every drawer render (see renderContents + the cart:refresh handler).
(function () {
  if (window.ehpCartTimerBoot) return;
  var running = {};

  function init(root) {
    var id = root.getAttribute('data-ct-id');
    if (running[id]) clearInterval(running[id]);

    var mins = parseInt(root.getAttribute('data-ct-mins'), 10) || 10;
    var onend = root.getAttribute('data-ct-onend') || 'repeat';
    var urgentAt = parseInt(root.getAttribute('data-ct-urgent'), 10) || 60;
    var clock = root.querySelector('[data-ct-clock]');
    var body = root.querySelector('.ehpct__body');
    var done = root.querySelector('[data-ct-done]');
    var design = !!(window.Shopify && window.Shopify.designMode);

    /* Key carries the minutes so changing the slider restarts the clock
       instead of reusing a deadline stored on an earlier visit. */
    var key = 'ehpct:' + id + ':' + mins;

    function deadline(reset) {
      if (design) {
        if (reset || !root.ehpSeed) root.ehpSeed = Date.now() + mins * 60000;
        return root.ehpSeed;
      }
      var saved = null;
      if (!reset) { try { saved = window.sessionStorage.getItem(key); } catch (e) {} }
      if (!saved) {
        saved = String(Date.now() + mins * 60000);
        try { window.sessionStorage.setItem(key, saved); } catch (e) {}
      }
      return parseInt(saved, 10);
    }

    var end = deadline(false);

    function finish() {
      if (onend === 'repeat') { end = deadline(true); return; }
      clearInterval(running[id]);
      if (onend === 'hide') { root.hidden = true; return; }
      if (onend === 'message') {
        if (body) body.hidden = true;
        if (done) done.hidden = false;
        root.classList.remove('is-urgent');
      }
    }

    function tick() {
      var left = Math.floor((end - Date.now()) / 1000);
      if (left <= 0) { finish(); return; }
      var m = Math.floor(left / 60);
      var s = left % 60;
      if (clock) clock.textContent = (m < 10 ? '0' : '') + m + ':' + (s < 10 ? '0' : '') + s;
      root.classList.toggle('is-urgent', left <= urgentAt);
    }

    tick();
    running[id] = setInterval(tick, 1000);
  }

  function boot() {
    var list = document.querySelectorAll('[data-ehp-cart-timer]');
    for (var i = 0; i < list.length; i++) init(list[i]);
  }

  window.ehpCartTimerBoot = boot;
  document.addEventListener('shopify:section:load', boot);
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();

// Block markup arrives via innerHTML, which never runs <script>, so re-boot the
// block scripts after the drawer re-renders.
document.addEventListener('cart:refresh', () => {
  if (window.ehpCartTimerBoot) window.ehpCartTimerBoot();
});

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble-count',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
